import { Box, Container, Divider, Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

const LAST_UPDATED = "April 24, 2026";

export default function RefundPolicyPage() {
  const { t } = useTranslation("pages-account");

  return (
    <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
      <Stack spacing={1} sx={{ mb: 5 }}>
        <Typography
          variant="overline"
          color="secondary.main"
          sx={{ fontWeight: 500, letterSpacing: "0.08em" }}
        >
          {t("RefundPolicyPage.eyebrow")}
        </Typography>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 500 }}>
          {t("RefundPolicyPage.heading")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("RefundPolicyPage.lastUpdated", { date: LAST_UPDATED })}
        </Typography>
      </Stack>

      <Stack spacing={4} sx={{ "& p": { lineHeight: 1.7 } }}>
        <Section title={t("RefundPolicyPage.policy.title")}>
          <Typography variant="body1">
            <Trans
              i18nKey="RefundPolicyPage.policy.nonRefundable"
              t={t}
              components={[<strong key="b0" />]}
            />
          </Typography>
          <Typography variant="body1">
            {t("RefundPolicyPage.policy.cancelTiming")}
          </Typography>
        </Section>

        <Section title={t("RefundPolicyPage.howToCancel.title")}>
          <Typography variant="body1">
            <Trans
              i18nKey="RefundPolicyPage.howToCancel.body"
              t={t}
              components={[<Link key="b0" component={RouterLink} to="/pricing" />]}
            />
          </Typography>
        </Section>

        <Section title={t("RefundPolicyPage.exceptions.title")}>
          <Typography variant="body1">
            {t("RefundPolicyPage.exceptions.intro")}
          </Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 1, lineHeight: 1.7 } }}>
            <li>
              <Trans
                i18nKey="RefundPolicyPage.exceptions.duplicate"
                t={t}
                components={[<strong key="b0" />]}
              />
            </li>
            <li>
              <Trans
                i18nKey="RefundPolicyPage.exceptions.unauthorized"
                t={t}
                components={[<strong key="b0" />]}
              />
            </li>
            <li>
              <Trans
                i18nKey="RefundPolicyPage.exceptions.unavailability"
                t={t}
                components={[<strong key="b0" />]}
              />
            </li>
          </Box>
        </Section>

        <Section title={t("RefundPolicyPage.chargebacks.title")}>
          <Typography variant="body1">
            <Trans
              i18nKey="RefundPolicyPage.chargebacks.body"
              t={t}
              components={[<Link key="b0" href="mailto:support@career-forge.org" />]}
            />
          </Typography>
        </Section>

        <Section title={t("RefundPolicyPage.contact.title")}>
          <Typography variant="body1">
            <Trans
              i18nKey="RefundPolicyPage.contact.body"
              t={t}
              components={[<Link key="b0" href="mailto:support@career-forge.org" />]}
            />
          </Typography>
        </Section>

        <Divider />

        <Typography variant="caption" color="text.secondary">
          <Trans
            i18nKey="RefundPolicyPage.footerNote"
            t={t}
            components={[<Link key="b0" component={RouterLink} to="/terms" />]}
          />
        </Typography>
      </Stack>
    </Container>
  );
}

function Section({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <Box component="section">
      {title && (
        <Typography variant="h5" component="h2" sx={{ fontWeight: 500, mb: 2 }}>
          {title}
        </Typography>
      )}
      <Stack spacing={2}>{children}</Stack>
    </Box>
  );
}
